import { useState, useRef } from 'react';
import { orderImportApi } from '../../api/orderImport.js';

const REQUIRED_COLUMNS = [
  { col: 'Client Name',                              req: true,  note: 'Customer / shop name' },
  { col: 'Client Phone Number',                      req: false, note: 'Primary phone (10 digits)' },
  { col: 'Client Phone Number 2',                    req: false, note: 'Secondary phone' },
  { col: 'GST Number',                               req: false, note: 'GST (15-char alphanumeric)' },
  { col: 'Client Address - Street Address',          req: false, note: 'Delivery address line 1' },
  { col: 'Client Address - Street Address Line 2',   req: false, note: 'Delivery address line 2' },
  { col: 'Client Address - City',                    req: false, note: 'Default: Kolkata' },
  { col: 'Client Address - State / Province',        req: false, note: 'Default: West Bengal' },
  { col: 'Client Address - Postal / Zip Code',       req: false, note: 'PIN code' },
  { col: 'Order ID',                                 req: false, note: 'External order reference' },
  { col: 'Invoice Number',                           req: false, note: 'e.g. MCCS/1234/26-27' },
  { col: 'Purchase Order No',                        req: false, note: 'Customer PO reference' },
  { col: 'Order Status',                             req: false, note: 'pending / delivered / cancelled / hold …' },
  { col: 'Grand Total',                              req: false, note: 'Total order amount (₹)' },
  { col: 'Delivery Date',                            req: false, note: 'Excel date serial or DD/MM/YYYY' },
  { col: 'Dispatch Date',                            req: false, note: 'Excel date serial or DD/MM/YYYY' },
  { col: 'Sales Executive Name',                     req: false, note: 'Name or email of executive' },
  { col: 'Product Category',                         req: false, note: 'local_retail / printed / retail_big / inter_state_retail' },
  { col: 'Mode of Delivery',                         req: false, note: 'e.g. courier, hand-delivery' },
  { col: 'Delivery Person',                          req: false, note: 'Driver / delivery agent name' },
  { col: 'Delivery Details',                         req: false, note: 'Free-text address note' },
  { col: 'Payment Method',                           req: false, note: 'cash / upi / cheque …' },
  { col: 'No of Trips',                              req: false, note: 'Number of delivery trips' },
  { col: 'Reason for Cancellation',                  req: false, note: 'Only if status = cancelled' },
  { col: 'Reason for Hold',                          req: false, note: 'Only if status = hold' },
  { col: 'Product 1 Name',                           req: false, note: 'First line item product name' },
  { col: 'Product 1 Rate',                           req: false, note: 'Unit price (₹)' },
  { col: 'Product 1 Qty',                            req: false, note: 'Quantity' },
  { col: 'Product 2 Name',                           req: false, note: 'Second product (same pattern up to Product 5)' },
];

const ORDER_STATUSES = [
  { value: '',                   label: 'Use value from Excel column' },
  { value: 'pending',            label: 'Pending' },
  { value: 'invoice_generated',  label: 'Invoice Generated' },
  { value: 'dispatched',         label: 'Dispatched' },
  { value: 'out_for_delivery',   label: 'Out for Delivery' },
  { value: 'delivered',          label: 'Delivered' },
  { value: 'cancelled',          label: 'Cancelled' },
  { value: 'hold',               label: 'Hold' },
  { value: 'returned',           label: 'Returned' },
  { value: 'deleted',            label: 'Deleted' },
];

export default function OrderImportPage() {
  const [file, setFile]               = useState(null);
  const [dragging, setDragging]       = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [progress, setProgress]       = useState(0);
  const [result, setResult]           = useState(null);
  const [error, setError]             = useState('');
  const [forceStatus, setForceStatus] = useState('');
  const inputRef = useRef();

  const pickFile = (f) => {
    if (!f) return;
    if (!/\.(xlsx|xls)$/i.test(f.name)) { setError('Only .xlsx or .xls files are accepted.'); return; }
    setFile(f); setError(''); setResult(null);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    pickFile(e.dataTransfer.files[0]);
  };

  const handleSubmit = async () => {
    if (!file) { setError('Please select an Excel file first.'); return; }
    setUploading(true); setError(''); setResult(null); setProgress(0);
    try {
      const res = await orderImportApi.upload(file, setProgress, forceStatus || null);
      setResult(res.data);
    } catch (err) {
      setError(err?.response?.data?.error || err.message || 'Import failed.');
    } finally {
      setUploading(false); setProgress(0);
    }
  };

  const reset = () => { setFile(null); setResult(null); setError(''); setForceStatus(''); };

  return (
    <div className="page-body">

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ margin: 0, fontWeight: 700, fontSize: '1.35rem' }}>Order Fulfillment Import</h2>
        <p style={{ margin: '6px 0 0', color: 'var(--text-muted)', fontSize: '0.88rem' }}>
          Upload an Excel file (.xlsx) with fulfilled order data. The system will match customers,
          create orders and items, and update lead records automatically.
        </p>
      </div>

      {/* Upload card */}
      {!result && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-body">
            <div
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onClick={() => !uploading && inputRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? 'var(--primary)' : 'var(--border)'}`,
                borderRadius: 10,
                padding: '36px 24px',
                textAlign: 'center',
                cursor: uploading ? 'not-allowed' : 'pointer',
                background: dragging ? 'rgba(99,102,241,0.04)' : 'var(--bg)',
                transition: 'border-color 0.15s, background 0.15s',
              }}
            >
              <div style={{ fontSize: '2.4rem', marginBottom: 8 }}>📂</div>
              {file ? (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>{file.name}</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>
                    {(file.size / 1024).toFixed(1)} KB · click to change
                  </div>
                </div>
              ) : (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.95rem' }}>Drop your Excel file here</div>
                  <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginTop: 4 }}>or click to browse (.xlsx, .xls)</div>
                </div>
              )}
              <input ref={inputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                onChange={e => pickFile(e.target.files?.[0])} />
            </div>

            {/* Force-status selector */}
            <div style={{ marginTop: 18 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: '0.85rem', marginBottom: 6 }}>
                Override Order Status
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {ORDER_STATUSES.map(s => (
                  <button
                    key={s.value}
                    type="button"
                    disabled={uploading}
                    onClick={() => setForceStatus(s.value)}
                    style={{
                      padding: '6px 14px',
                      borderRadius: 20,
                      fontSize: '0.82rem',
                      fontWeight: forceStatus === s.value ? 700 : 400,
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      border: forceStatus === s.value
                        ? '2px solid var(--primary)'
                        : '1.5px solid var(--border)',
                      background: forceStatus === s.value
                        ? 'rgba(99,102,241,0.10)'
                        : 'var(--bg)',
                      color: forceStatus === s.value ? 'var(--primary)' : 'var(--text-muted)',
                      transition: 'all 0.15s',
                    }}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              {forceStatus && (
                <div style={{ marginTop: 7, fontSize: '0.8rem', color: '#d97706' }}>
                  ⚠ Every order in this file will be set to <strong>{forceStatus}</strong>, ignoring the "Order Status" column.
                </div>
              )}
              {!forceStatus && (
                <div style={{ marginTop: 7, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Each order will use the status written in the "Order Status" column (or <em>pending</em> if blank).
                </div>
              )}
            </div>

            {error && (
              <div style={{ marginTop: 14, padding: '10px 14px', borderRadius: 7, background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '0.87rem' }}>
                {error}
              </div>
            )}

            {uploading && (
              <div style={{ marginTop: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.82rem', marginBottom: 6, color: 'var(--text-muted)' }}>
                  <span>Importing data…</span><span>{progress}%</span>
                </div>
                <div style={{ height: 8, borderRadius: 4, background: 'var(--border)', overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${progress}%`, background: 'var(--primary)', borderRadius: 4, transition: 'width 0.3s' }} />
                </div>
                <div style={{ marginTop: 8, fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                  Large files may take 1–2 minutes. Please do not close this tab.
                </div>
              </div>
            )}

            <div style={{ marginTop: 18, display: 'flex', gap: 10, alignItems: 'center' }}>
              <button
                className="btn btn-primary"
                onClick={handleSubmit}
                disabled={!file || uploading}
                style={!file || uploading ? { opacity: 0.45, cursor: 'not-allowed' } : {}}
              >
                {uploading ? 'Importing…' : 'Start Import'}
              </button>
              {file && !uploading && (
                <button className="btn btn-outline" onClick={reset}>Clear</button>
              )}
              {!file && !uploading && (
                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Select a file to continue</span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Result card */}
      {result && (
        <div className="card" style={{ marginBottom: 24, borderLeft: '4px solid var(--primary)' }}>
          <div className="card-body">
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
              <span style={{ fontSize: '1.5rem' }}>✅</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>Import Complete</div>
                <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>
                  {result.forced_status
                    ? <>All orders imported as <strong style={{ color: 'var(--primary)' }}>{result.forced_status}</strong></>
                    : 'All rows processed successfully'}
                </div>
              </div>
              <button className="btn btn-outline btn-sm" style={{ marginLeft: 'auto' }} onClick={reset}>Import Another</button>
            </div>

            {/* Summary grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Rows Processed',   value: result.summary.rows_processed,  color: '#6366f1' },
                { label: 'Orders Created',    value: result.summary.orders_created,  color: '#16a34a' },
                { label: 'Status Updated',    value: result.summary.status_updated,  color: '#0891b2' },
                { label: 'Items Created',     value: result.summary.items_created,   color: '#16a34a' },
                { label: 'Leads Matched',     value: result.summary.leads_matched,   color: '#0891b2' },
                { label: 'Fuzzy Matched',     value: result.summary.leads_fuzzy,     color: '#d97706' },
                { label: 'Leads Created',     value: result.summary.leads_created,   color: '#7c3aed' },
                { label: 'Locations Added',   value: result.summary.locations_added, color: '#059669' },
                { label: 'GST Updated',       value: result.summary.gst_updated,     color: '#64748b' },
                { label: 'Skipped Rows',      value: result.summary.skipped_rows,    color: '#ef4444' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ padding: '12px 14px', borderRadius: 8, background: 'var(--bg)', border: '1.5px solid var(--border)', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 800, color }}>{value}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 2 }}>{label}</div>
                </div>
              ))}
            </div>

            {result.fuzzy_matches?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, color: '#d97706' }}>
                  ⚠ Fuzzy-matched rows — please verify these:
                </div>
                <div style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: 7, padding: '10px 14px', fontSize: '0.82rem', maxHeight: 180, overflowY: 'auto' }}>
                  {result.fuzzy_matches.map((m, i) => <div key={i} style={{ padding: '2px 0' }}>{m}</div>)}
                </div>
              </div>
            )}

            {result.new_leads?.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', marginBottom: 8, color: '#7c3aed' }}>
                  New leads created:
                </div>
                <div style={{ background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 7, padding: '10px 14px', fontSize: '0.82rem', maxHeight: 180, overflowY: 'auto' }}>
                  {result.new_leads.map((m, i) => <div key={i} style={{ padding: '2px 0' }}>{m}</div>)}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Column reference */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ fontSize: '0.97rem' }}>Expected Column Reference</h3>
          <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Row 1 must be the header row</span>
        </div>
        <div className="card-body" style={{ padding: 0 }}>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table" style={{ fontSize: '0.82rem' }}>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Column Name</th>
                  <th style={{ textAlign: 'center' }}>Required</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                {REQUIRED_COLUMNS.map((c, i) => (
                  <tr key={c.col}>
                    <td className="text-muted">{i + 1}</td>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600, whiteSpace: 'nowrap' }}>{c.col}</td>
                    <td style={{ textAlign: 'center' }}>
                      {c.req
                        ? <span style={{ color: '#ef4444', fontWeight: 700 }}>Yes</span>
                        : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>{c.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
